import { useQuery } from '@apollo/client';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { legacyPrograms, useLegacyAccess, type LegacyProgram } from '@/pages/legacy/shared';
import { ParticipantProfile } from './ParticipantProfilePage';
import { ParticipantProfileSearchPage } from './ParticipantProfileSearchPage';
import { PROFILE_REFERENCE, type ProfileView } from './queries';
import { profileProgram } from './programPresentation';

/** The route carries program context only for the React presentation. */
export const LegacyParticipantProfile = ({ program, participant, initialSection }: { program: LegacyProgram; participant: number; initialSection?: ProfileView }) => {
  const access = useLegacyAccess();
  const valid = Number.isSafeInteger(participant) && participant > 0 && profileProgram(participant) === program;
  const allowed = access('program:participant~read');
  const result = useQuery<{ participantProfileForProgramNumber: { odrnumber: number | null } | null }>(PROFILE_REFERENCE, {
    variables: { participant }, skip: !valid || !allowed, fetchPolicy: 'network-only',
  });
  if (!valid) return <p role="alert">Enter a participant number for this program.</p>;
  if (!allowed) return <p role="alert">Profile access is unavailable.</p>;
  if (result.loading) return <p role="status">Opening profile…</p>;
  if (result.error) return <p role="alert">{result.error.message}</p>;
  const odrnumber = result.data?.participantProfileForProgramNumber?.odrnumber;
  if (!odrnumber) return <p role="alert">The participant profile is unavailable.</p>;
  return <ParticipantProfile key={odrnumber} odrnumber={odrnumber} initialProgram={program} initialSection={initialSection} initialParticipant={participant} />;
};

const legacySections: Readonly<Record<string, ProfileView>> = {
  legal: 'Legal', medical: 'Medical', plan_location: 'Plan', notifications: 'Notifications', coordinated_care: 'Care',
  notes: 'Notes', incidents: 'Incidents', major_life_events: 'Events', insurance_enrollment: 'Enrollment',
};
export const legacyProfileView = (program: LegacyProgram, params: URLSearchParams): ProfileView | undefined => {
  const tab = params.get('tab');
  if (tab && legacySections[tab]) return program === 'lead' && tab === 'legal' ? 'Enrollment' : legacySections[tab];
  if (['new-insurance', 'delete'].includes(params.get('action') ?? '')) return 'Enrollment';
  if (params.has('deletecase') || params.has('deletehearing')) return 'Legal';
  const display = params.get('display')?.slice(0, 9); const index = display?.indexOf('Y') ?? -1;
  if (index < 0) return undefined;
  if (index === 0 && program === 'lead') return 'Enrollment';
  return Object.values(legacySections)[index];
};

export const LegacyParticipantProfileRoute = ({ program }: { program: LegacyProgram }) => {
  const { participant } = useParams();
  const [params] = useSearchParams();
  const number = participant ?? params.get('participant') ?? params.get(program === 'mist' ? 'id' : 'pid');
  const cid = params.get('cid')?.trim();
  const search = (!number || program !== 'mist') && cid ? cid : undefined;
  const openProfile = number != null && !(program !== 'mist' && cid);
  const view = legacyProfileView(program, params);
  return <section id={`legacy-${program}-shared-profile`} className="space-y-3">
    <nav aria-label="Legacy profile navigation" className="flex gap-2 p-4 pb-0">
      <Button asChild variant="outline"><Link id={`legacy-${program}-shared-search`} to={openProfile ? `/legacy/${program}` : '/legacy/search'}><Search aria-hidden="true" className="mr-2 h-4 w-4" />{openProfile ? 'Program search' : 'Global search'}</Link></Button>
      <Button asChild variant="outline"><Link id={`legacy-${program}-shared-dashboard`} to="/dashboard"><ArrowLeft aria-hidden="true" className="mr-2 h-4 w-4" />Main menu</Link></Button>
    </nav>
    {openProfile ? <LegacyParticipantProfile key={`${number}-${view ?? 'Identity'}`} program={program} participant={Number(number)} initialSection={view} />
      : <ParticipantProfileSearchPage key={`${program}-${search ?? ''}`} program={program} initialSearch={search} />}
  </section>;
};

export const SharedLegacySearchPage = () => <section id="legacy-shared-search" className="space-y-3">
  <nav aria-label="Legacy search navigation" className="flex flex-wrap gap-2 p-4 pb-0">
    <Button asChild variant="outline"><Link id="legacy-shared-search-dashboard" to="/dashboard"><ArrowLeft aria-hidden="true" className="mr-2 h-4 w-4" />Main menu</Link></Button>
    {legacyPrograms.map(program => <Button key={program.key} asChild variant="outline"><Link id={`legacy-shared-search-${program.key}`} to={`/legacy/${program.key}`}><Search aria-hidden="true" className="mr-2 h-4 w-4" />{program.label}</Link></Button>)}
  </nav>
  <ParticipantProfileSearchPage />
</section>;

export const SharedLegacyMistPage = () => <LegacyParticipantProfileRoute program="mist" />;
export const SharedLegacyFistPage = () => <LegacyParticipantProfileRoute program="fist" />;
export const SharedLegacyOdrHousingPage = () => <LegacyParticipantProfileRoute program="odrhousing" />;
export const SharedLegacyLeadPage = () => <LegacyParticipantProfileRoute program="lead" />;
export const SharedLegacyPrgWmnPage = () => <LegacyParticipantProfileRoute program="prg_wmn" />;
export const SharedLegacyDshPage = () => <LegacyParticipantProfileRoute program="dsh" />;
